import React, { useEffect, useRef, useState } from 'react';
import { ui } from '../ui';
import { formatDateTimeET, formatDurationSeconds } from '../format';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { Category, TranscriptDetail, TranscriptSegment, getTranscriptDetail } from '../api';

interface TranscriptViewerProps {
  callId: number;
  categories: Category[];
  onClose: () => void;
}

const categoryLabel = (id: number, categories: Category[]) =>
  categories.find(c => c.CategoryID === id)?.Code ?? `#${id}`;

// One speaker turn. SpeakerRole distinguishes Agent vs. Caller with BOTH a text label and
// alignment (never color alone, per WCAG) — Agent turns sit left, Caller turns sit right, and
// each turn always carries its role name regardless of any color styling.
const TranscriptTurn: React.FC<{ segment: TranscriptSegment }> = ({ segment }) => {
  const isAgent = segment.SpeakerRole === 'Agent';
  return (
    <li className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[85%] rounded-lg border px-3 py-2 text-sm ${
          isAgent
            ? 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950'
            : 'border-border-default bg-surface'
        }`}
      >
        <div className="mb-1 flex items-baseline gap-2 text-xs">
          <span className={`font-semibold ${isAgent ? 'text-blue-700 dark:text-blue-300' : 'text-secondary'}`}>
            {segment.SpeakerRole}
          </span>
          <span className="text-muted">{segment.SpeakerName}</span>
          <span className="font-mono text-muted">{segment.StartTs}</span>
        </div>
        <p className="whitespace-pre-wrap text-primary">{segment.Text}</p>
      </div>
    </li>
  );
};

// Read-only spot-check viewer (FR Group 6 / OQ-5 v1 scope) — modal detail for one call. Fetched
// calls show the speaker-attributed transcript; every other FetchStatus shows the status and
// LastError instead of pretending there's transcript content to browse.
export const TranscriptViewer: React.FC<TranscriptViewerProps> = ({ callId, categories, onClose }) => {
  const [detail, setDetail] = useState<TranscriptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getTranscriptDetail(callId)
      .then(setDetail)
      .catch(() => setError('Could not load this call’s transcript — try again.'))
      .finally(() => setLoading(false));
  }, [callId]);

  // Focus the close control on open; Escape closes, mirroring CategoryMultiSelect's convention.
  useEffect(() => {
    closeButtonRef.current?.focus();
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCopy = async () => {
    if (!detail?.TranscriptText) return;
    try {
      await navigator.clipboard.writeText(detail.TranscriptText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to the clipboard.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="transcript-viewer-title"
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-border-default bg-surface-alt shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border-default p-4">
          <div>
            <h2 id="transcript-viewer-title" className="text-lg font-semibold text-primary">
              Call Transcript
            </h2>
            {detail && (
              <p className="mt-1 text-sm text-muted">
                {formatDateTimeET(detail.CallTimeUtc)} &middot; {detail.AgentName ?? (detail.AgentID ? `Agent #${detail.AgentID}` : 'Unassigned')}
                {' '}&middot; {formatDurationSeconds(detail.DurationSeconds)}
                {detail.CategoryIDs.length > 0 && (
                  <> &middot; {detail.CategoryIDs.map(id => categoryLabel(id, categories)).join(', ')}</>
                )}
              </p>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className={ui.btnSecondary}
            onClick={onClose}
            aria-label="Close transcript viewer"
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && <LoadingSpinner size="sm" message="Loading transcript…" />}
          {!loading && error && (
            <p role="alert" className="text-sm text-red-600">{error}</p>
          )}
          {!loading && !error && detail && (
            <>
              <div className="mb-3 flex items-center gap-2">
                <StatusBadge status={detail.FetchStatus} />
                {detail.FetchStatus === 'Fetched' && (
                  <span className="text-xs text-muted">
                    {detail.Segments.length} segment{detail.Segments.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>

              {detail.FetchStatus === 'Fetched' ? (
                detail.Segments.length > 0 ? (
                  <ul className="space-y-3">
                    {detail.Segments.map((seg, i) => (
                      <TranscriptTurn key={`${seg.StartTs}-${i}`} segment={seg} />
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted">
                    Fetched, but no speaker-attributed segments were returned for this call.
                  </p>
                )
              ) : (
                <div className="rounded-md border border-border-default bg-surface p-3 text-sm">
                  <p className="text-secondary">
                    {detail.FetchStatus === 'NotAttempted' && 'No fetch has been attempted for this call yet.'}
                    {detail.FetchStatus === 'NoRecording' && 'This call has no recording — no transcript is possible.'}
                    {detail.FetchStatus === 'NoTranscriptAvailable' && 'A recording exists, but Zoom has no transcript for this call.'}
                    {detail.FetchStatus === 'Blocked' && 'Blocked by Zoom — the transcript scope was missing or revoked at fetch time.'}
                    {detail.FetchStatus === 'Failed' && 'The last fetch attempt failed.'}
                  </p>
                  {detail.LastError && (
                    <p className="mt-2 break-words text-xs text-muted">{detail.LastError}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {!loading && !error && detail?.FetchStatus === 'Fetched' && detail.TranscriptText && (
          <div className="flex items-center justify-end gap-2 border-t border-border-default p-3">
            {copied && <span className="text-sm text-muted">Copied.</span>}
            <button type="button" className={ui.btnSecondary} onClick={handleCopy}>
              Copy text
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
